<template>
<!-- HEADER WITH CONTROLS -->
<div class="mb-4">
  <h6 class="text-white fw-semibold mb-3 mt-3">Emergency Contacts & Location</h6>

  <!-- TOP ROW: MUNICIPALITY + UTILITY -->
  <div class="d-flex flex-wrap justify-content-between gap-4 mb-2">
    <div>
      <p class="text-white-50 text-xxs text-uppercase mb-0">Municipality</p>
      <p class="text-white fw-semibold mb-0">
        {{ municipality }}
      </p>
    </div>

    <div>
      <p class="text-white-50 text-xxs text-uppercase mb-0">Water/Elect. Utility</p>
      <p class="text-white fw-semibold mb-0">
        {{ waterUtility }}
      </p>
    </div>
  </div>

  <!-- TARIFF REGIME -->
  <div>
    <p class="text-white-50 text-xxs text-uppercase mb-0">Tariff Regime</p>
    <p class="text-white fw-semibold text-xs mb-0">
      {{ tariffRegime }}
    </p>
  </div>
</div>

<!-- MAP SECTION -->
<div
  ref="map_wrapper"
  class="w-100 map-height rounded-4 overflow-hidden border border-secondary position-relative"
>

  <!-- GOOGLE MAP -->
  <div ref="mapContainer" class="w-100 h-100 rounded-4"></div>

  <!-- LOADING OVERLAY -->
  <div
    v-if="mapLoading"
    class="position-absolute top-0 start-0 w-100 h-100
           d-flex align-items-center justify-content-center
           map-loading-overlay text-center"
  >
    <div>
      <p class="text-white fw-semibold mb-1">
        Loading Map & Nearby Services
      </p>
      <p class="text-white-50 text-xs mb-0">
        Searching within 5km radius...
      </p>
    </div>
  </div>

  <!-- MAP ERROR OVERLAY -->
  <div
    v-else-if="mapError"
    class="position-absolute top-0 start-0 w-100 h-100
           d-flex align-items-center justify-content-center
           map-error-overlay text-center"
  >
    <div>
      <p class="text-white-50 text-xs mb-0">
        Unable to load map
      </p>
      <small class="text-white-50">
        {{ locationName }}
      </small>
    </div>
  </div>

  <!-- CLICK TO INTERACT OVERLAY -->
  <div
    v-else-if="show_map_overlay"
    class="position-absolute top-0 start-0 w-100 h-100
           d-flex align-items-center justify-content-center
           map-interact-overlay"
    @click.stop="activate_map"
  >
    <div class="text-center">
      <i class="bi bi-hand-index-thumb fs-2 text-white mb-2 d-block"></i>
      <p class="text-white fw-semibold mb-0">
        Click to interact with the map
      </p>
    </div>
  </div>

</div>


<!-- CURRENT PROPERTY SHOPPING CENTER (IF APPLICABLE) -->
<div v-if="propertyShoppingCenter" class="mb-4">
  <div class="d-flex justify-content-between align-items-center mb-3">
    <h6 class="text-white fw-semibold mb-0">
      <i class="fas fa-store text-success me-2"></i>
      Current Property Shopping Center
    </h6>
    <span class="badge bg-success">Your Property</span>
  </div>
  
  <!-- Property Shopping Center Card -->
  <div class="col-12">
    <div class="glass-card p-3 rounded-4 h-100 shadow-sm border border-success">
      <div class="d-flex justify-content-between align-items-start mb-2">
        <div class="d-flex align-items-start flex-grow-1">
          <i class="fas fa-store text-success me-2 mt-1"></i>
          <div class="flex-grow-1">
            <div class="text-white fw-semibold text-xs">
              {{ getPropertyDisplayName(propertyShoppingCenter) }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- Property Anchor Tenants - WITH SHOW MORE/LESS -->
      <div v-if="propertyAnchorTenants && propertyAnchorTenants.length > 0" class="mb-3">
        <small class="text-white-50 d-block mb-1">Confirmed Anchor Tenants:</small>
        <div class="d-flex flex-wrap gap-1 mb-1">
          <span
            v-for="(tenant, tenantIndex) in getVisibleAnchorTenants(propertyShoppingCenter)"
            :key="tenantIndex"
            class="badge text-xs"
            :class="getTenantCategoryClass(tenant.category)"
          >
            {{ tenant.name }}
          </span>
        </div>
        
        <!-- Show More/Less Button -->
        <button
          v-if="shouldShowMoreAnchorTenants(propertyShoppingCenter)"
          @click="toggleAnchorTenants(propertyShoppingCenter)"
          class="btn btn-sm btn-outline-success px-2 py-0 text-xs"
        >
          view more
        </button>
        <button
          v-else-if="propertyAnchorTenants.length > 3"
          @click="toggleAnchorTenants(propertyShoppingCenter)"
          class="btn btn-sm btn-outline-success px-2 py-0 text-xs"
        >
          Show less
        </button>
      </div>
      
      <!-- ETA & DISTANCE BADGE -->
      <div class="mb-3 p-2 rounded-2 bg-dark border border-secondary">
        <div class="row g-1">
          <div class="col-6">
            <small class="text-white-50 d-block">
              <i class="bi bi-clock-fill text-info me-1"></i>
            </small>
            <small class="text-white fw-semibold">
              {{ propertyShoppingCenter.eta || 'N/A' }}
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- COMPETITOR SHOPPING CENTERS SECTION -->
<div v-if="competitorShoppingCenters.length > 0" class="mb-4">
  <div class="d-flex justify-content-between align-items-center mb-3">
    <h6 class="text-white fw-semibold mb-0">
      <i class="fas fa-shopping-cart text-purple me-2"></i>
      Nearby Competitor Shopping Centers
    </h6>
    <span class="badge bg-purple">{{ competitorShoppingCenters.length }} found</span>
  </div>
  
  <!-- First 3 competitor centers -->
  <div class="row g-3">
    <div
      v-for="(center, index) in visibleCompetitorCenters"
      :key="center.place_id"
      class="col-12 col-md-6 col-lg-4"
    >
      <div
        class="glass-card p-3 rounded-4 h-100 shadow-sm border"
        :class="{
          'border-danger': center.competitor_score >= 80,
          'border-warning': center.competitor_score >= 60 && center.competitor_score < 80,
          'border-info': center.competitor_score >= 40 && center.competitor_score < 60,
          'border-secondary': center.competitor_score < 40
        }"
      >
        <div class="d-flex justify-content-between align-items-start mb-2">
          <div class="d-flex align-items-start flex-grow-1">
            <i class="fas fa-shopping-cart me-2 mt-1" 
               :class="{
                 'text-danger': center.competitor_score >= 80,
                 'text-warning': center.competitor_score >= 60 && center.competitor_score < 80,
                 'text-info': center.competitor_score >= 40 && center.competitor_score < 60,
                 'text-secondary': center.competitor_score < 40
               }"></i>
            <div class="flex-grow-1">
              <div class="text-white fw-semibold text-xs">
                {{ center.name }}
              </div>
              <div class="text-white-50 text-xs">
                {{ center.vicinity || center.formatted_address || 'Address not available' }}
              </div>
            </div>
          </div>
        </div>
        
        <!-- Competitor Anchor Tenants - WITH SHOW MORE/LESS -->
        <div v-if="center.anchor_tenants && center.anchor_tenants.length > 0" class="mb-3">
          <small class="text-white-50 d-block mb-1">Anchor Tenants:</small>
          <div class="d-flex flex-wrap gap-1 mb-1">
            <span
              v-for="(tenant, tenantIndex) in getVisibleAnchorTenants(center)"
              :key="tenantIndex"
              class="badge text-xs"
              :class="getTenantCategoryClass(tenant.category)"
              :title="getTenantTooltip(tenant)"
            >
              {{ tenant.name }}{{ tenant.assumed ? '*' : '' }}
            </span>
          </div>
          
          <!-- Show More/Less Button -->
          <button
            v-if="shouldShowMoreAnchorTenants(center)"
            @click="toggleAnchorTenants(center)"
            class="btn btn-sm px-2 py-0 text-xs"
            :class="{
              'btn-outline-danger': center.competitor_score >= 80,
              'btn-outline-warning': center.competitor_score >= 60 && center.competitor_score < 80,
              'btn-outline-info': center.competitor_score >= 40 && center.competitor_score < 60,
              'btn-outline-secondary': center.competitor_score < 40
            }"
          >
            view more
          </button>
          <button
            v-else-if="center.anchor_tenants.length > 3"
            @click="toggleAnchorTenants(center)"
            class="btn btn-sm px-2 py-0 text-xs"
            :class="{
              'btn-outline-danger': center.competitor_score >= 80,
              'btn-outline-warning': center.competitor_score >= 60 && center.competitor_score < 80,
              'btn-outline-info': center.competitor_score >= 40 && center.competitor_score < 60,
              'btn-outline-secondary': center.competitor_score < 40
            }"
          >
            Show less
          </button>
        </div>
        
        <!-- ETA & DISTANCE BADGE -->
        <div class="mb-3 p-2 rounded-2 bg-dark border border-secondary">
          <div class="row g-1">
            <div class="col-6">
              <small class="text-white-50 d-block">
                <i class="bi bi-clock-fill text-info me-1"></i>
              </small>
              <small class="text-white fw-semibold">
                {{ center.eta || 'N/A' }}
              </small>
            </div>
            <div class="col-6">
              <small class="text-white-50 d-block">
                <i class="bi bi-geo-alt text-warning me-1"></i>
              </small>
              <small class="text-white fw-semibold">
                {{ formatDistance(center.distance) }}
              </small>
            </div>
          </div>
        </div>
        
        <!-- Competition Level & Actions -->
        <div class="d-flex justify-content-between align-items-center mt-2 pt-2 border-top border-secondary">
          <span
            class="badge text-xs"
            :class="getCompetitionLevelClass(center.competitor_score)"
          >
            Competition: {{ getCompetitionLevelText(center.competitor_score) }}
          </span>
          <div class="d-flex gap-2">
            <button
              @click="toggleDirections(center)"
              class="btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
              :class="showRoutes[center.name] ? 'btn-info' : 'btn-outline-info'"
              style="width: 32px; height: 32px;"
              :title="showRoutes[center.name] ? 'Hide Route' : 'Show Route'"
            >
              <i class="bi bi-geo-alt"></i>
            </button>
            <a
              v-if="center.website"
              :href="center.website"
              target="_blank"
              class="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
              style="width: 32px; height: 32px;"
              title="Open Website"
            >
              <i class="bi bi-globe2"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Show All Competitor Centers Button -->
  <div v-if="competitorShoppingCenters.length > 3" class="text-center mt-3">
    <button
      @click="showAllCompetitorCenters = !showAllCompetitorCenters"
      class="btn btn-sm btn-outline-purple"
    >
      <i class="bi" :class="showAllCompetitorCenters ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      {{ showAllCompetitorCenters ? 'Show Less' : `Show All ${competitorShoppingCenters.length} Competitor Centers` }}
    </button>
  </div>
  
  <!-- ALL COMPETITOR CENTERS (EXPANDED VIEW) -->
  <div v-if="showAllCompetitorCenters && competitorShoppingCenters.length > 3" class="mt-4 pt-4 border-top border-secondary">
    <h6 class="text-white fw-semibold mb-3">All Nearby Competitor Centers</h6>
    <div class="row g-3">
      <div
        v-for="(center, index) in remainingCompetitorCenters"
        :key="'all-' + center.place_id"
        class="col-12 col-md-6 col-lg-4"
      >
        <div
          class="glass-card p-3 rounded-4 h-100 shadow-sm border"
          :class="{
            'border-danger': center.competitor_score >= 80,
            'border-warning': center.competitor_score >= 60 && center.competitor_score < 80,
            'border-info': center.competitor_score >= 40 && center.competitor_score < 60,
            'border-secondary': center.competitor_score < 40
          }"
        >
          <div class="d-flex justify-content-between align-items-start mb-2">
            <div class="d-flex align-items-start flex-grow-1">
              <i class="fas fa-shopping-cart me-2 mt-1"
                 :class="{
                   'text-danger': center.competitor_score >= 80,
                   'text-warning': center.competitor_score >= 60 && center.competitor_score < 80,
                   'text-info': center.competitor_score >= 40 && center.competitor_score < 60,
                   'text-secondary': center.competitor_score < 40
                 }"></i>
              <div class="flex-grow-1">
                <div class="text-white fw-semibold text-xs">
                  {{ center.name }}
                </div>
                <div class="text-white-50 text-xs">
                  {{ center.vicinity || 'Address not available' }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- Anchor Tenants WITH SHOW MORE/LESS -->
          <div v-if="center.anchor_tenants && center.anchor_tenants.length > 0" class="mb-2">
            <small class="text-white-50 d-block mb-1">Anchor Tenants:</small>
            <div class="d-flex flex-wrap gap-1 mb-1">
              <span
                v-for="(tenant, tenantIndex) in getVisibleAnchorTenants(center)"
                :key="tenantIndex"
                class="badge text-xs"
                :class="getTenantCategoryClass(tenant.category)"
                :title="getTenantTooltip(tenant)"
              >
                {{ tenant.name }}{{ tenant.assumed ? '*' : '' }}
              </span>
            </div>
            
            <!-- Show More/Less Button -->
            <button
              v-if="shouldShowMoreAnchorTenants(center)"
              @click="toggleAnchorTenants(center)"
              class="btn btn-sm px-2 py-0 text-xs"
              :class="{
                'btn-outline-danger': center.competitor_score >= 80,
                'btn-outline-warning': center.competitor_score >= 60 && center.competitor_score < 80,
                'btn-outline-info': center.competitor_score >= 40 && center.competitor_score < 60,
                'btn-outline-secondary': center.competitor_score < 40
              }"
            >
              view more
            </button>
            <button
              v-else-if="center.anchor_tenants.length > 3"
              @click="toggleAnchorTenants(center)"
              class="btn btn-sm px-2 py-0 text-xs"
              :class="{
                'btn-outline-danger': center.competitor_score >= 80,
                'btn-outline-warning': center.competitor_score >= 60 && center.competitor_score < 80,
                'btn-outline-info': center.competitor_score >= 40 && center.competitor_score < 60,
                'btn-outline-secondary': center.competitor_score < 40
              }"
            >
              Show less
            </button>
          </div>
          
          <!-- ETA & Distance -->
          <div class="d-flex justify-content-between align-items-center mb-2">
            <small class="text-white-50">
              <i class="bi bi-clock me-1"></i>{{ center.eta || 'N/A' }}
            </small>
            <small class="text-white-50">
              <i class="bi bi-geo-alt me-1"></i>{{ formatDistance(center.distance) }}
            </small>
          </div>
          
          <!-- Actions -->
          <div class="d-flex justify-content-between align-items-center mt-2 pt-2 border-top border-secondary">
            <span
              class="badge text-xs"
              :class="getCompetitionLevelClass(center.competitor_score)"
            >
              {{ getCompetitionLevelText(center.competitor_score) }}
            </span>
            <div class="d-flex gap-1">
              <button
                @click="toggleDirections(center)"
                class="btn btn-sm"
                :class="showRoutes[center.name] ? 'btn-info' : 'btn-outline-info'"
                :title="showRoutes[center.name] ? 'Hide Route' : 'Show Route'"
              >
                <i class="bi bi-geo-alt"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- EMERGENCY CONTACTS -->
<div class="mt-3">
  <div class="d-flex justify-content-between align-items-center mb-3">
    <h6 class="text-white fw-semibold mb-0">
      <i class="bi bi-phone-vibrate-fill text-primary me-2"></i>
      Emergency Services <i>(Voice Dial coming 2027)</i>
      <span v-if="hasServicesOutsideRadius" class="ms-2">
        <i class="bi bi-exclamation-triangle-fill text-warning" 
           title="Some services found outside 5km radius"></i>
      </span>
    </h6>
    <div>
      <span class="badge bg-primary me-2">
        {{ nearestEmergencyServices.length }} found
      </span>
      <span v-if="hasServicesOutsideRadius" class="badge bg-warning text-dark">
        Expanded search
      </span>
    </div>
  </div>
  
  <!-- IF CONTACTS FOUND -->
  <div v-if="nearestEmergencyServices.length > 0" class="row g-3">
    <div
      v-for="(place, index) in nearestEmergencyServices"
      :key="index"
      class="col-12 col-md-6 col-lg-4"
    >
      <div
        class="glass-card p-3 rounded-4 h-100 shadow-sm border position-relative"
        :style="{ borderLeft: `4px solid ${routeColors[index % routeColors.length]}` }"
      >
        <!-- Outside Radius Indicator -->
        <div v-if="place.outsideStandardRadius" 
             class="position-absolute top-0 end-0 m-2"
             data-bs-toggle="tooltip" 
             :title="`Found outside 5km radius (${Math.round(place.searchRadius/1000)}km)`">
          <span class="badge bg-warning text-dark text-xs">
            <i class="bi bi-signpost-fill me-1"></i>
            {{ Math.round(place.searchRadius/1000) }}km
          </span>
        </div>
        
        <!-- Service Name with Distance Indicator -->
        <div class="d-flex justify-content-between align-items-start mb-2">
          <div class="d-flex align-items-start flex-grow-1">
            <i
              :class="place.icon"
              class="fs-5 me-2"
              :style="{ color: routeColors[index % routeColors.length] }"
            ></i>

            <div class="flex-grow-1">
              <div class="d-flex align-items-center gap-2">
                <div class="text-white fw-semibold text-xs">
                  {{ place.name }}
                </div>
                <span v-if="place.searchRadius > 5000" class="text-warning text-xxs">
                  (outside 5km)
                </span>
              </div>

              <div class="text-white-50 text-xs">
                {{ place.vicinity || place.formatted_address }}
              </div>

              <div
                v-if="place.formatted_phone_number"
                class="text-white-50 text-xs"
              >
                {{ format_phone_number(place.formatted_phone_number) }}
              </div>
            </div>
          </div>
        </div>

        <!-- ETA & DISTANCE BADGE with Radius Info -->
        <div class="mb-3 p-2 rounded-2 bg-dark border border-secondary">
          <div class="row g-1">
            <div class="col-6">
              <small class="text-white-50 d-block">
                <i class="bi bi-clock-fill text-info me-1"></i>
                ETA
              </small>
              <small class="text-white fw-semibold">
                {{ place.eta || 'N/A' }}
              </small>
            </div>
            <div class="col-6">
              <small class="text-white-50 d-block">
                <i class="bi bi-geo-alt text-warning me-1"></i>
                Distance
              </small>
              <small class="text-white fw-semibold">
                {{ formatDistance(place.distance) }}
                <span v-if="place.searchRadius > 5000" class="text-warning text-xxs ms-1">
                  ({{ Math.round(place.searchRadius/1000) }}km search)
                </span>
              </small>
            </div>
          </div>
        </div>

        <!-- ACTION TOOLBAR -->
        <div class="mt-2 pt-2 border-top border-secondary d-flex justify-content-between align-items-center gap-2">
          <!-- Main Route Toggle -->
          <button
            @click="toggleDirections(place)"
            :class="showRoutes[place.name] ? 'btn-primary' : 'btn-outline-primary'"
            class="btn btn-sm rounded-pill text-xs fw-semibold d-flex align-items-center gap-1 px-3 flex-grow-1"
          >
            <i :class="showRoutes[place.name] ? 'bi bi-eye-fill' : 'bi bi-geo-alt-fill'"></i>
            <span>{{ showRoutes[place.name] ? 'Hide route' : 'Show route' }}</span>
          </button>

          <!-- Icon actions -->
          <div class="d-flex align-items-center gap-2">
            <!-- Call -->
            <button
              v-if="place.formatted_phone_number"
              @click="callPlace(place)"
              class="btn btn-sm btn-dark border border-secondary rounded-circle d-flex align-items-center justify-content-center"
              type="button"
              data-bs-toggle="tooltip"
              title="Call"
              style="width: 32px; height: 32px;"
            >
              <i class="bi bi-telephone-fill"></i>
            </button>

            <!-- Website -->
            <a
              v-if="place.website"
              :href="place.website"
              target="_blank"
              class="btn btn-sm btn-dark border border-secondary rounded-circle d-flex align-items-center justify-content-center"
              data-bs-toggle="tooltip"
              title="Open website"
              style="width: 32px; height: 32px;"
            >
              <i class="bi bi-globe2"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- IF NO CONTACTS -->
  <div v-else class="text-white-50 text-xs text-center py-4">
    <i class="bi bi-search fs-4 d-block mb-2"></i>
    No emergency services found. Try refreshing the search.
  </div>
  
  <!-- Search Radius Info -->
  <div v-if="nearestEmergencyServices.length > 0" class="mt-3 pt-3 border-top border-secondary">
    <small class="text-white-50 text-xs d-flex align-items-center">
      <i class="bi bi-info-circle me-2"></i>
      <span v-if="hasServicesOutsideRadius">
        <span class="text-warning">Some services were found outside the 5km radius.</span> Expanded search performed up to 200km.
      </span>
      <span v-else>
        All services found within 5km radius.
      </span>
    </small>
  </div>
</div>
</template>